import { Navigate, createBrowserRouter, Outlet, RouteObject } from 'react-router-dom';

import { EventFormDialog } from '@components';

import Layout from '../layout';

import LostPage from 'pages/404/LostPage';

import RegisterSpotPage from 'pages/register';
import EventsPage from 'pages/events/EventsPage';
import MakersPage from 'pages/makers/MakersPage';
import SpotsPage from 'pages/spots/SpotsPage';
import { useSession } from '@hooks';
import { RequireMeRole, RequireRole } from '.';
import ErrorBoundary from './ErrorBoudary';

import { MePage, Table } from 'pages/me';

import {
  OAuthPage,
  ActivatePage,
  MyCalendarPage,
  MyDashboardPage,
  MyBookingDetailPage,
  MyEventDetailPage,
  SupportPage,
  SupportDashboardPage,
  SupportAccountsdPage,
  SupportAccountDetail,
  SearchPage,
  TosPage,
  PrivacyPolicyPage,
} from '../pages';

export const buildRouter = (session: ReturnType<typeof useSession>) => {
  const { account } = session;
  const role = account?.__typename ?? 'Guest';

  const meRoleRoutesMap: Record<string, RouteObject[]> = {
    Guest: [],
    Consumer: [
      {
        id: 'me-calendar',
        path: 'calendar',
        element: (
          <RequireMeRole role={role} allowedRoles={['Consumer']}>
            <MyCalendarPage />
          </RequireMeRole>
        ),
        children: [
          {
            id: 'me-calendar-booking-exact',
            path: 'booking', // Exact path
            element: <Navigate to="/me/calendar" replace />,
          },
          {
            id: 'me-calendar-booking-detail',
            path: 'booking/:bookingId/detail', // Path with :bookingId
            element: <MyBookingDetailPage />,
          },
          {
            id: 'me-calendar-booking-history',
            path: 'booking/:bookingId/history', // Path with :bookingId
            element: <MyBookingDetailPage />,
          },
          {
            id: 'me-calendar-event-exact',
            path: 'event', // Exact path
            element: <Navigate to="/me/calendar" replace />,
          },
          {
            id: 'me-calendar-event-create',
            path: 'event/create',
            element: <EventFormDialog variant="create" />,
          },
          {
            id: 'me-calendar-event-detail',
            path: 'event/:eventId/detail',
            element: <MyEventDetailPage />,
          },
        ],
      },
      {
        id: 'me-table',
        path: 'table',
        element: <Table />,
        children: [
          {
            id: 'me-table-booking-exact',
            path: 'booking', // Exact path
            element: <Navigate to="/me/table" replace />,
          },
          {
            id: 'me-table-booking-detail',
            path: 'booking/:bookingId/detail',
            element: <MyBookingDetailPage />,
          },
          {
            id: 'me-table-event-exact',
            path: 'event', // Exact path
            element: <Navigate to="/me/table" replace />,
          },
          {
            id: 'me-table-event-detail',
            path: 'event/:eventId/detail',
            element: <MyEventDetailPage />,
          },
        ],
      },
    ],
    Spot: [
      {
        id: 'me-dashboard',
        path: 'dashboard',
        element: <Outlet />,
        children: [
          {
            id: 'me-dashboard-preview',
            path: 'preview',
            element: <MyDashboardPage />,
          },
        ],
      },
      {
        id: 'me-calendar',
        path: 'calendar',
        element: (
          <RequireMeRole role={role} allowedRoles={['Consumer', 'Spot']}>
            <Outlet />
          </RequireMeRole>
        ),
        children: [
          {
            id: 'me-calendar-booking',
            path: 'booking',
            element: <MyCalendarPage />,
            children: [
              {
                id: 'me-calendar-booking-detail',
                path: ':bookingId/detail',
                element: <MyBookingDetailPage />,
              },
              {
                id: 'me-calendar-booking-history',
                path: ':bookingId/history',
                element: <MyBookingDetailPage />,
              },
            ],
          },
          {
            id: 'me-calendar-event',
            path: 'event',
            element: <MyCalendarPage />,
            children: [
              {
                id: 'me-calendar-event-create',
                path: 'create',
                element: <EventFormDialog variant="create" />,
              },
              {
                id: 'me-calendar-event-detail',
                path: ':eventId/detail',
                element: <MyEventDetailPage />,
              },
            ],
          },
        ],
      },
    ],
  };

  return createBrowserRouter([
    {
      id: 'app',
      path: '/',
      element: <Layout />,
      ErrorBoundary,
      children: [
        // UNLLOGGED USER OR CONSUMER PAGES
        {
          index: true,
          element: (
            <>
              {role === 'Guest' && <Navigate to="/spots" />}
              {role === 'Consumer' && <Navigate to="/spots" />}
              {role === 'Spot' && <Navigate to="/me" />}
              {role === 'Support' && <Navigate to="/support" />}
            </>
          ),
          // element: (role === 'Spot' ? <Navigate to="/me" /> : <Navigate to="/spots" />),
        },
        // REGISTER PRO ACCOUNT
        {
          id: 'register',
          path: 'register',
          element: <Outlet />,
          children: [
            {
              id: 'register-spot',
              path: 'spot',
              element: (
                <RequireRole role={role} allowedRoles={['Guest', 'Spot', 'Consumer', 'Support']}>
                  <RegisterSpotPage />
                </RequireRole>
              ),
            },
          ],
        },
        {
          id: 'events',
          path: 'events',
          element: (
            <RequireRole role={role} allowedRoles={['Guest', 'Consumer']}>
              <EventsPage />
            </RequireRole>
          ),
          children: [
            {
              id: 'event',
              path: ':eventId',
              element: <></>,
            },
          ],
        },
        {
          id: 'spots',
          path: 'spots',
          element: (
            <RequireRole role={role} allowedRoles={['Guest', 'Consumer']}>
              <SpotsPage />
            </RequireRole>
          ),
          children: [
            {
              id: 'spot',
              path: ':slug',
              element: <></>,
            },
          ],
        },
        {
          id: 'artists',
          path: 'artists',
          element: (
            <RequireRole role={role} allowedRoles={['Guest', 'Consumer']}>
              <MakersPage />
            </RequireRole>
          ),
        },
        {
          id: 'search',
          path: 'search',
          element: (
            <RequireRole role={role} allowedRoles={['Guest', 'Consumer']}>
              <SearchPage />
            </RequireRole>
          ),
          children: [
            {
              id: 'search-spot',
              path: 'spot',
              element: <Navigate to="/search?search=spots&page=1" replace />,
            },
            {
              id: 'search-spot-open',
              path: 'spot/:slug',
              element: <></>,
            },
            {
              id: 'search-event',
              path: 'event',
              element: <Navigate to="/search?search=events&page=1" replace />,
            },
            {
              id: 'search-event-open',
              path: 'event/:eventId',
              element: <></>,
            },
            {
              id: 'search-member',
              path: 'member',
              element: <Navigate to="/search?search=members&page=1" replace />,
            },
            {
              id: 'search-member-open',
              path: 'member/:memberSlug',
              element: <></>,
            },
          ],
        },
        // LOGGED USER
        {
          id: 'me',
          path: 'me',
          element: (
            <RequireMeRole role={role} allowedRoles={['Consumer', 'Spot']}>
              {/*
                 MePage is responsible of :
                - redirecting "me" exact match route depending on user role 
                - redirecting desktop views to mobile views if device is mobile
              */}
              <MePage />
            </RequireMeRole>
          ),
          children: meRoleRoutesMap[role],
        },
        // OAUTH FINALIZATION
        {
          id: 'oauth',
          path: 'oauth',
          children: [
            {
              index: true,
              element: <Navigate to="/oauth/success" replace />,
            },
            {
              id: 'oauth-success',
              path: 'success',
              element: <OAuthPage />,
            },
          ],
        },
        // EMAIL ACTIVATION
        {
          id: 'activate',
          path: 'activate',
          element: <ActivatePage />,
        },
        // SUPPORT
        {
          id: 'support',
          path: 'support',
          children: [
            {
              id: 'support-dashboard',
              path: 'dashboard',
              element: (
                <RequireRole role={role} allowedRoles={['Support']}>
                  <SupportDashboardPage />
                </RequireRole>
              ),
            },
            {
              id: 'support-users',
              path: 'users',
              element: (
                <RequireRole role={role} allowedRoles={['Support']}>
                  <SupportPage page="users" />
                </RequireRole>
              ),
            },
            {
              id: 'support-accounts',
              path: 'accounts',
              element: (
                <RequireRole role={role} allowedRoles={['Support']}>
                  <SupportAccountsdPage />
                </RequireRole>
              ),
              children: [
                {
                  id: 'support-accounts-overview',
                  path: ':sSlug',
                  element: (
                    <RequireRole role={role} allowedRoles={['Support']}>
                      <SupportAccountDetail />
                    </RequireRole>
                  ),
                  children: [
                    {
                      id: 'support-accounts-detail',
                      path: ':sTab',
                      element: (
                        <RequireRole role={role} allowedRoles={['Support']}>
                          <SupportAccountDetail />
                        </RequireRole>
                      ),
                    },
                  ],
                },
              ],
            },
            {
              id: 'support-events',
              path: 'events',
              element: (
                <RequireRole role={role} allowedRoles={['Support']}>
                  <SupportPage page="events" />
                </RequireRole>
              ),
            },
            {
              id: 'support-report',
              path: 'reports',
              element: (
                <RequireRole role={role} allowedRoles={['Support']}>
                  <SupportPage page="report" />
                </RequireRole>
              ),
            },
          ],
        },
        {
          id: 'terms-of-service',
          path: 'terms-of-service',
          element: <TosPage />,
        },
        {
          id: 'privacy-policy',
          path: 'privacy-policy',
          element: <PrivacyPolicyPage />,
        },
        // FOOTER
        {
          id: '404',
          path: '*',
          element: <LostPage />,
        },
      ],
    },
  ]);
};
